import { Injectable } from '@angular/core';
import { BatchUpdateFileType } from '@app/app.constants';
import { FileUploadType, getUploadFileRouteName, rawBodyFileUploadTypes } from '@core/models/file.model';
import { BaseService } from '@core/services/base/base.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService extends BaseService {
  public uploadProgress = new BehaviorSubject(0);

  constructor(private errorHandlerService: ErrorHandlerService, private authService: AuthService) {
    super();
  }

  upload(file: File, inputName: FileUploadType, filter?: { fileType: number; kind?: 'source' }) {
    const isRawBody = rawBodyFileUploadTypes.includes(inputName);
    const isGenericUpload = inputName === FileUploadType.BatchUpdateContext || inputName === FileUploadType.BatchUpdatePrices;
    const params = isRawBody ? { ...filter, fileName: file.name } : filter;
    let url = getUploadFileRouteName(inputName);
    if (params) {
      url += '?' + this.getParamsFromFilter(params);
    }

    return new Observable<string>(observer => {
      if (isGenericUpload) {
        const validFileTypes = inputName === FileUploadType.BatchUpdateContext
          ? [BatchUpdateFileType.gridContext, BatchUpdateFileType.gridContextWeekType, BatchUpdateFileType.gridContextDateBased]
          : [BatchUpdateFileType.pricesWeekType, BatchUpdateFileType.pricesDateBased];
        if (!filter || !validFileTypes.includes(filter.fileType) || filter.kind !== 'source') {
          this.errorHandlerService.showErrorMessage([], 400);
          observer.error(new Error('Invalid fileType or kind for source upload.'));
          return;
        }
      }
      const request = new XMLHttpRequest();
      request.open('POST', url, true);

      const token = this.authService.getToken();
      request.setRequestHeader('Authorization', 'Bearer ' + token.access_token);

      request.onreadystatechange = () => {
        this.updateFileUploadProgress(request.readyState);

        if (request.readyState === 4) {
          if (request.status === 200) {
            let fileName = file.name;

            try {
              const response = JSON.parse(request.responseText);
              const responseFileName = inputName === FileUploadType.BatchUpdateThema
                ? response?.data?.fileName
                : isGenericUpload ? response?.data : response?.data?.fileName ?? response?.data;
              if (typeof responseFileName === 'string' && responseFileName.trim()) {
                fileName = responseFileName;
              } else if (isGenericUpload || inputName === FileUploadType.BatchUpdateThema) {
                throw new Error('Missing filename in upload response.');
              }
            } catch (error) {
              if (isGenericUpload || inputName === FileUploadType.BatchUpdateThema) {
                this.errorHandlerService.showErrorMessage([], 200);
                observer.error(error);
                return;
              }
            }

            observer.next(fileName);
            observer.complete();
          } else {
            this.errorHandlerService.showErrorMessage(
              [],
              request.status,
              'Impossible de télécharger le fichier. Veuillez essayer plus tard.'
            );
            observer.error('Impossible to upload the file.');
          }
        }
      };

      if (isRawBody) {
        request.setRequestHeader('Content-Type', 'application/octet-stream');
        request.send(file);
      } else {
        const formData = new FormData();
        formData.append('file', file);
        request.send(formData);
      }
    });
  }

  updateFileUploadProgress(readyState) {
    switch (readyState) {
      case 2:
        this.uploadProgress.next(33);
        break;
      case 3:
        this.uploadProgress.next(66);
        break;
      case 4:
        this.uploadProgress.next(100);
        break;
    }
  }
}
